import { REGION_LABELS, EMPLOYMENT_TYPE_LABELS, TENDER_TYPE_LABELS } from './taxonomy';
import { KIND_LABELS } from './labels';
import { Deadline } from './deadline';
import { GateStrip } from './GateStrip';
import { isBackgroundClick } from './row-click';
import type { PostingListItem } from './types';

/**
 * One posting, as a card.
 *
 * TWO COLUMNS: everything you read on one side, the deadline on the other. The deadline used to be
 * the last line of a paragraph — "מועד אחרון להגשה: 10/09/2026 · נותרו 12 ימים" — which put the one
 * value the whole product exists to surface at the end of a sentence, in body text, indistinguishable
 * from the publisher's name above it. Lifted into its own column it is scannable down a list of
 * twenty without reading any of them.
 *
 * It renders through `<Deadline>`, the SAME component the table cell uses. Two spellings of one
 * value is how a card and a row end up disagreeing about which posting is urgent.
 *
 * THE WHOLE CARD OPENS THE POSTING, not just the title. A card is one object on screen — the
 * publisher, the badges and the countdown are all part of the same thing the reader is deciding
 * about — so a click anywhere on it means "this one". The title stays a real button because that is
 * what a keyboard tabs to and what tells the eye the card is openable at all; the card is not given
 * a `role` or a tab stop of its own, which would announce an entire posting as one button and put a
 * second stop on every item in the list.
 */
export function PostingCard({ item, onOpen }: { item: PostingListItem; onOpen: () => void }) {
  const subtype =
    item.kind === 'tender'
      ? item.tenderType && TENDER_TYPE_LABELS[item.tenderType]
      : item.employmentType && EMPLOYMENT_TYPE_LABELS[item.employmentType];

  return (
    <article
      className="tenders-card"
      onClick={(event) => {
        if (isBackgroundClick(event)) onOpen();
      }}
    >
      <div className="tenders-card__main">
        <h2 className="tenders-card__title">
          <button type="button" className="tenders-card__link" onClick={onOpen}>
            {item.title}
          </button>
        </h2>

        <p className="tenders-card__publisher">
          {item.source.publisher}
          {item.city && ` · ${item.city}`}
        </p>

        <p className="tenders-card__meta">
          <span className="badge">{KIND_LABELS[item.kind]}</span>
          {item.categoryLabel && <span className="badge">{item.categoryLabel}</span>}
          {item.region && <span className="badge">{REGION_LABELS[item.region]}</span>}
          {subtype && <span className="badge">{subtype}</span>}
          {item.securityClearanceRequired === true && <span className="badge badge-accent">דרוש סיווג ביטחוני</span>}
        </p>

        <GateStrip item={item} />
      </div>

      <Deadline iso={item.dates.lastSubmissionDate} />
    </article>
  );
}
